import React, { useCallback, useMemo } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { Carousel } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronRight } from '@fortawesome/free-solid-svg-icons';
import { FormattedMessage, useIntl } from 'react-intl';

import { Routes } from '@share/constants';

import type { ImageCarouselProps } from './types';
import { getDefaultCarouselSlides, getItemTypeFromSlideId, getSlideImageSrc } from './utils';

import styles from './styles.module.scss';

const CATALOG_ITEM_TYPE_PARAM = 'itemType';

export const ImageCarousel = (props: ImageCarouselProps): React.ReactElement => {
  const intl = useIntl();
  const { items: itemsProp, onExploreClick } = props;
  const history = useHistory();
  const { accountUsername } = useParams<{ accountUsername?: string }>();
  const items = useMemo(() => itemsProp ?? getDefaultCarouselSlides(), [itemsProp]);

  const handleExploreClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      const slideId = e.currentTarget.getAttribute('data-slide-id');
      const slide = slideId ? items.find((s) => s.id === slideId) : undefined;
      if (!slide) {
        return;
      }
      const itemType = getItemTypeFromSlideId(slide.id);
      if (!itemType || !accountUsername) {
        return;
      }
      const search = new URLSearchParams({ [CATALOG_ITEM_TYPE_PARAM]: itemType }).toString();
      history.push(`/${accountUsername}${Routes.CertificatesCatalog}?${search}`);
    },
    [history, accountUsername, items],
  );

  const carouselSettings = useMemo(
    () => ({
      dots: { className: styles.dots },
      infinite: true,
      speed: 500,
      slidesToShow: 1,
      slidesToScroll: 1,
      arrows: true,
    }),
    [],
  );

  return (
    <div className={styles.wrapper}>
      <Carousel {...carouselSettings}>
        {items.map((slide) => (
          <div key={slide.id} className={styles.slide}>
            <div className={styles.contentBlock}>
              <h2 className={styles.heading}>
                {slide.headingId
                  ? intl.formatMessage({ id: slide.headingId })
                  : slide.heading ?? ''}
              </h2>
              <p className={styles.description}>
                {slide.descriptionId
                  ? intl.formatMessage({ id: slide.descriptionId })
                  : slide.description ?? ''}
              </p>
              <button
                type="button"
                className={styles.exploreButton}
                data-slide-id={slide.id}
                onClick={onExploreClick ? onExploreClick : handleExploreClick}
                aria-label={intl.formatMessage({ id: 'certificates.carousel.explore' })}
              >
                <FormattedMessage id="certificates.carousel.explore" />
                <FontAwesomeIcon icon={faChevronRight} />
              </button>
            </div>
            <div className={styles.imageBlock}>
              <img
                src={getSlideImageSrc(slide.imageSrc)}
                alt={
                  slide.imageAltId
                    ? intl.formatMessage({ id: slide.imageAltId })
                    : slide.imageAlt ?? ''
                }
                className={styles.image}
                loading="lazy"
              />
            </div>
          </div>
        ))}
      </Carousel>
    </div>
  );
};
